import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  getPageByUserId,
  createPage,
  updatePage,
  addWidget,
  updateWidget,
  deleteWidget,
  reorderWidgets,
  type UpdatePageInput,
} from '@/services/pages';
import type { Page, Widget, WidgetType, WidgetData } from '@/types';

export const usePage = () => {
  const { user } = useAuth();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async () => {
    if (!user) {
      setPage(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let userPage = await getPageByUserId(user.uid);

      // Create page if it doesn't exist
      if (!userPage) {
        const username = user.username || user.email?.split('@')[0] || 'user';
        userPage = await createPage({
          userId: user.uid,
          username,
          title: user.displayName || username,
          avatarUrl: user.photoURL || undefined,
        });
      }

      setPage(userPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch page');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const update = async (input: UpdatePageInput) => {
    if (!page) throw new Error('No page to update');

    try {
      const updatedPage = await updatePage(page.id, input);
      setPage(updatedPage);
      return updatedPage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update page';
      setError(message);
      throw err;
    }
  };

  const addNewWidget = async (type: WidgetType, data: WidgetData) => {
    if (!page) throw new Error('No page to add widget to');

    try {
      const updatedPage = await addWidget(page.id, { type, data });
      setPage(updatedPage);
      return updatedPage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add widget';
      setError(message);
      throw err;
    }
  };

  const editWidget = async (widgetId: string, data: Partial<Widget>) => {
    if (!page) throw new Error('No page to edit widget in');

    try {
      const updatedPage = await updateWidget(page.id, widgetId, data);
      setPage(updatedPage);
      return updatedPage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update widget';
      setError(message);
      throw err;
    }
  };

  const removeWidget = async (widgetId: string) => {
    if (!page) throw new Error('No page to remove widget from');

    try {
      const updatedPage = await deleteWidget(page.id, widgetId);
      setPage(updatedPage);
      return updatedPage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete widget';
      setError(message);
      throw err;
    }
  };

  const reorder = async (widgetIds: string[]) => {
    if (!page) throw new Error('No page to reorder widgets in');

    try {
      const updatedPage = await reorderWidgets(page.id, widgetIds);
      setPage(updatedPage);
      return updatedPage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder widgets';
      setError(message);
      throw err;
    }
  };

  return {
    page,
    loading,
    error,
    update,
    addWidget: addNewWidget,
    editWidget,
    removeWidget,
    reorderWidgets: reorder,
    refresh: fetchPage,
  };
};
